import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close, AddPhotoAlternate, Delete } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import {
  barePhone,
  formatPhoneInput,
  validatePhoneNumber as validatePhoneField,
} from '../utils/phoneValidation';

interface StudioFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  studio?: any; // If provided, it's edit mode
}

const StudioFormDialog: React.FC<StudioFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  studio,
}) => {
  const isEditMode = Boolean(studio);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  // OTP step for create mode: 'details' | 'otp'
  const [step, setStep] = useState<'details' | 'otp'>('details');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    studioName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    studioType: '',
    description: '',
    workingHoursFrom: '',
    workingHoursTo: '',
    longitude: '',
    latitude: '',
    images: [] as string[],
    logo: '',
  });

  useEffect(() => {
    if (open) {
      setStep('details');
      setOtpSent(false);
      setOtp('');
      setPassword('');
      setError(null);
      if (isEditMode && studio) {
        // Populate form with studio data
        setFormData({
          studioName: studio.studioName || '',
          email: studio.email || '',
          phoneNumber: barePhone(studio.phoneNumber || ''),
          address: studio.address || '',
          city: studio.city || '',
          state: studio.state || '',
          pincode: studio.pincode || '',
          studioType: studio.studioType || '',
          description: studio.description || '',
          workingHoursFrom: studio.workingHoursFrom
            ? (() => {
                // Working hours are stored as IST serialised as UTC
                // (admin entered "11:00" IST → stored as 1970-01-01T11:00Z)
                // Read back the UTC components directly so the form shows the
                // same IST value the admin originally typed.
                const d = new Date(studio.workingHoursFrom);
                return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
              })()
            : '',
          workingHoursTo: studio.workingHoursTo
            ? (() => {
                const d = new Date(studio.workingHoursTo);
                return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
              })()
            : '',
          longitude: studio.location?.coordinates?.[0]?.toString() || '',
          latitude: studio.location?.coordinates?.[1]?.toString() || '',
          images: studio.images || [],
          logo: studio.logo || '',
        });
      } else {
        // Reset form for new studio
        setFormData({
          studioName: '',
          email: '',
          phoneNumber: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          studioType: '',
          description: '',
          workingHoursFrom: '',
          workingHoursTo: '',
          longitude: '',
          latitude: '',
          images: [],
          logo: '',
        });
      }
      setError(null);
    }
  }, [open, studio, isEditMode]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError(null);
    try {
      const res: any = await apiService.uploadFiles(files, 'studios');
      const urls: string[] = res?.fileLinks || [];
      handleChange('images', [...formData.images, ...urls]);
    } catch (err: any) {
      setError(err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    handleChange('images', formData.images.filter((_: string, i: number) => i !== index));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError(null);
    try {
      const res: any = await apiService.uploadFiles([file], 'studios');
      const url: string | undefined = res?.fileLinks?.[0];
      if (!url) throw new Error('Upload did not return a file link');
      handleChange('logo', url);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email || !formData.studioName || !password) {
      setError('Studio name, email and password are required.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await apiService.generateStudioSignupOtp(formData.email);
      setOtpSent(true);
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    const phoneError = validatePhoneField(formData.phoneNumber, { required: false });
    if (phoneError) {
      setError(phoneError);
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && studio) {
        const payload: any = {
          studioName: formData.studioName,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          studioType: formData.studioType,
          description: formData.description,
          images: formData.images,
          // Sent even when empty so clearing the logo actually persists.
          logo: formData.logo,
        };
        if (formData.workingHoursFrom) payload.workingHoursFrom = formData.workingHoursFrom;
        if (formData.workingHoursTo) payload.workingHoursTo = formData.workingHoursTo;
        if (formData.longitude && formData.latitude) {
          payload.longitude = parseFloat(formData.longitude);
          payload.latitude = parseFloat(formData.latitude);
        }
        await apiService.updateStudio(studio._id, payload);
      } else {
        if (!otp) {
          setError('Please enter the OTP sent to the studio email.');
          setLoading(false);
          return;
        }
        await apiService.createStudio({
          studioName: formData.studioName,
          email: formData.email,
          password,
          otp,
          ...(formData.logo ? { logo: formData.logo } : {}),
        });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving studio:', err);
      setError(err.message || 'Failed to save studio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {isEditMode ? 'Edit Studio' : 'Create Studio'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {otpSent && !isEditMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            An OTP has been sent to <strong>{formData.email}</strong>. Enter it below to complete registration.
          </Alert>
        )}

        <Box sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* OTP step — shown after OTP is sent in create mode */}
            {!isEditMode && step === 'otp' && (
              <TextField
                fullWidth
                label="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={loading}
                helperText="Enter the 6-digit OTP sent to the studio email"
                inputProps={{ maxLength: 6 }}
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Studio Name"
                value={formData.studioName}
                onChange={(e) => handleChange('studioName', e.target.value)}
                required
                disabled={loading || step === 'otp'}
                sx={{ flex: '1 1 300px' }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                disabled={loading || isEditMode || step === 'otp'}
                sx={{ flex: '1 1 300px' }}
              />
            </Box>
            {!isEditMode && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading || step === 'otp'}
                helperText="Set the login password for this studio"
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Phone Number"
                placeholder="9876543210"
                value={formData.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', formatPhoneInput(e.target.value))}
                disabled={loading}
                sx={{ flex: '1 1 300px' }}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: (
                    <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      +91
                    </Typography>
                  ),
                }}
                helperText="10-digit phone number"
              />
              <FormControl fullWidth sx={{ flex: '1 1 300px' }}>
                <InputLabel>Studio Type</InputLabel>
                <Select
                  value={formData.studioType}
                  onChange={(e) => handleChange('studioType', e.target.value)}
                  label="Studio Type"
                  disabled={loading}
                >
                  <MenuItem value="wellness">Wellness</MenuItem>
                  <MenuItem value="workout">Workout</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              disabled={loading}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                disabled={loading}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                disabled={loading}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                fullWidth
                label="Pincode"
                value={formData.pincode}
                onChange={(e) => handleChange('pincode', e.target.value)}
                disabled={loading}
                sx={{ flex: '1 1 200px' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Working Hours From"
                type="time"
                value={formData.workingHoursFrom}
                onChange={(e) => handleChange('workingHoursFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                sx={{ flex: '1 1 300px' }}
              />
              <TextField
                fullWidth
                label="Working Hours To"
                type="time"
                value={formData.workingHoursTo}
                onChange={(e) => handleChange('workingHoursTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                sx={{ flex: '1 1 300px' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
                onWheel={stopWheelNudge}
                disabled={loading}
                sx={{ flex: '1 1 300px' }}
              />
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
                onWheel={stopWheelNudge}
                disabled={loading}
                sx={{ flex: '1 1 300px' }}
              />
            </Box>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={loading}
            />

            {/* Studio Logo — optional, printed on receipts and invoices */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Studio Logo <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Shown on receipts and invoices. PNG or JPG, square works best.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploadingLogo ? <CircularProgress size={14} /> : <AddPhotoAlternate />}
                  onClick={() => logoInputRef.current?.click()}
                  disabled={loading || uploadingLogo}
                >
                  {uploadingLogo ? 'Uploading...' : formData.logo ? 'Replace Logo' : 'Upload Logo'}
                </Button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
              </Box>

              {formData.logo ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      bgcolor: '#fff',
                    }}
                  >
                    <img
                      src={formData.logo}
                      alt="Studio logo"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete sx={{ fontSize: 16 }} />}
                    onClick={() => handleChange('logo', '')}
                    disabled={loading || uploadingLogo}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="caption" display="block">
                    No logo — receipts will show the studio name as text
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Studio Images */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Studio Images
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload photos shown in the app (max 10 recommended)
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={14} /> : <AddPhotoAlternate />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Photos'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
              </Box>

              {formData.images.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 1,
                  }}
                >
                  {formData.images.map((url: string, i: number) => (
                    <Box
                      key={i}
                      sx={{
                        position: 'relative',
                        aspectRatio: '1',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover .remove-btn': { opacity: 1 },
                      }}
                    >
                      <img
                        src={url}
                        alt={`Studio ${i + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {i === 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            left: 4,
                            bgcolor: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.75,
                          }}
                        >
                          Cover
                        </Box>
                      )}
                      <Tooltip title="Remove">
                        <IconButton
                          className="remove-btn"
                          size="small"
                          onClick={() => handleRemoveImage(i)}
                          disabled={loading || uploading}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            opacity: 0,
                            transition: 'opacity 0.15s',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            p: 0.4,
                            '&:hover': { bgcolor: 'rgba(200,0,0,0.75)' },
                          }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <AddPhotoAlternate sx={{ fontSize: 32, opacity: 0.4, mb: 0.5 }} />
                  <Typography variant="caption" display="block">
                    No images yet — click "Upload Photos" to add
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        {!isEditMode && step === 'otp' && (
          <Button onClick={() => { setStep('details'); setOtp(''); }} disabled={loading}>
            Back
          </Button>
        )}
        {!isEditMode && step === 'details' ? (
          <Button
            onClick={handleSendOtp}
            variant="contained"
            disabled={loading || !formData.studioName || !formData.email || !password}
          >
            {loading ? <CircularProgress size={22} /> : 'Send OTP & Continue'}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (!isEditMode && !otp)}
          >
            {loading ? <CircularProgress size={22} /> : isEditMode ? 'Update Studio' : 'Create Studio'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StudioFormDialog;

